import "./index.css";
import "./App.css";
import { useEffect, useRef, useState } from "react";

// Custom hook to detect when element enters viewport
const useInView = (ref, threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, threshold]);

  return isInView;
};

function App() {
  const cursorRef = useRef(null);
  const [leftCode, setLeftCode] = useState("");
  const [leftIndex, setLeftIndex] = useState(0);
  const [rightCode, setRightCode] = useState("");
  const [rightIndex, setRightIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [heroOffset, setHeroOffset] = useState(0);
  
  // Refs for scroll animations
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const skillsRef = useRef(null);
  const projectsRef = useRef(null);
  const experienceRef = useRef(null);
  const githubRef = useRef(null);
  const contactRef = useRef(null);
  
  // Detect when sections come into view
  const heroInView = useInView(heroRef);
  const aboutInView = useInView(aboutRef);
  const skillsInView = useInView(skillsRef);
  const projectsInView = useInView(projectsRef);
  const experienceInView = useInView(experienceRef);
  const contactInView = useInView(contactRef);


  // Project filter state
  const [selectedFilter, setSelectedFilter] = useState("All");

  // Animated counter state
  const [stats, setStats] = useState({ projects: 0, years: 0, clients: 0 });

  // Contact form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeSection, setActiveSection] = useState("about");
  const [showBackToTop, setShowBackToTop] = useState(false);

  const projectsData = [
    {
      title: "Football Team Management",
      description: "Full-featured system tracking players, matches, attendance, and performance.",
      techs: ["PHP", "SQL"],
      image: "https://via.placeholder.com/640x360?text=Football+Manager",
      live: "https://example.com/football",
      repo: "https://github.com/iud2003/football-manager"
    },
    {
      title: "Vulnerable Web App",
      description: "Learned cybersecurity by building a deliberately vulnerable app.",
      techs: ["React", "Node.js"],
      image: "https://via.placeholder.com/640x360?text=Vulnerable+App",
      live: "https://example.com/vuln-app",
      repo: "https://github.com/iud2003/vuln-app"
    },
    {
      title: "Inventory Management System",
      description: "Tracks products, stock, and sales efficiently.",
      techs: ["JavaScript", "SQL"],
      image: "https://via.placeholder.com/640x360?text=Inventory+System",
      live: "https://example.com/inventory",
      repo: "https://github.com/iud2003/inventory"
    },
  ];

  const filteredProjects = projectsData.filter(
    (project) => selectedFilter === "All" || project.techs.includes(selectedFilter)
  );

  const leftSnippets = [
    "const portfolio = {\n  name: 'Isum Uthsara',\n  title: 'Full-Stack Developer',\n  experience: '3+ years',\n  specialization: 'Web Development'\n};\n\n",
    "function buildAwesomeWebsite() {\n  const design = createModernUI();\n  const backend = setupDatabase();\n  const frontend = developReactApp();\n  const deploy = launchOnServer();\n  return deploy(frontend, backend);\n}\n\n",
    "class Developer {\n  constructor(name) {\n    this.name = name;\n    this.skills = ['React', 'Node.js', 'MongoDB'];\n    this.passion = 'Coding';\n    this.goal = 'Innovation';\n  }\n  code() { return 'create amazing apps'; }\n}\n\n",
  ];

  const rightSnippets = [
    "// Building the future\nconst technology = {\n  frontend: 'React, Tailwind CSS',\n  backend: 'Node.js, Express',\n  database: 'MongoDB, SQL',\n  tools: 'Git, Docker, AWS'\n};\n\n",
    "async function deployApp() {\n  const testing = await runTests();\n  const build = await createBuild();\n  const deployment = await upload();\n  const success = await verify();\n  return success ? 'Live!' : 'Retry';\n}\n\n",
    "const journey = {\n  started: 2021,\n  projects: 50,\n  clients: 'Happy',\n  learning: 'Never Stop',\n  nextGoal: 'Full-Stack Master'\n};\n\n",
  ];

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    const handleMove = (e) => {
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };

    window.addEventListener("mousemove", handleMove);

    return () => {
      window.removeEventListener("mousemove", handleMove);
    };
  }, []);

  // Page loading simulation
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(t);
  }, []);

  // Parallax hero background + Scroll Spy
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setHeroOffset(Math.min(y * 0.3, 120));
      setShowBackToTop(y > 300);

      // Scroll Spy - detect which section is in view
      const sections = [
        { id: "about", ref: aboutRef },
        { id: "skills", ref: skillsRef },
        { id: "projects", ref: projectsRef },
        { id: "experience", ref: experienceRef },
        { id: "github", ref: githubRef },
        { id: "contact", ref: contactRef },
      ];

      for (let section of sections) {
        if (section.ref.current) {
          const rect = section.ref.current.getBoundingClientRect();
          // Check if section is in viewport (top of section visible)
          if (rect.top < window.innerHeight * 0.5 && rect.bottom > 0) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  // Animated statistics counter
  useEffect(() => {
    const interval = setInterval(() => {
      setStats((prev) => ({
        projects: prev.projects < 50 ? prev.projects + 2 : 50,
        years: prev.years < 3 ? prev.years + 0.1 : 3,
        clients: prev.clients < 15 ? prev.clients + 1 : 15,
      }));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Contact form handler
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus("Please fill in all fields");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setFormStatus("Please enter a valid email");
      return;
    }
    const subject = encodeURIComponent(`Portfolio Contact from ${formData.name}`);
    const body = encodeURIComponent(`${formData.message}\n\nReply-to: ${formData.email}`);
    window.location.href = `mailto:isumuthsara2003@gmail.com?subject=${subject}&body=${body}`;
    setFormStatus("Opening email client...");
    setTimeout(() => setFormStatus(""), 3000);
  };

  // Left side typing
  useEffect(() => {
    const fullLeftText = leftSnippets.join("");
    if (leftIndex < fullLeftText.length) {
      const timeout = setTimeout(() => {
        setLeftCode(fullLeftText.slice(0, leftIndex + 1));
        setLeftIndex(leftIndex + 1);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      const resetTimeout = setTimeout(() => {
        setLeftCode("");
        setLeftIndex(0);
      }, 2000);
      return () => clearTimeout(resetTimeout);
    }
  }, [leftIndex]);

  // Right side typing
  useEffect(() => {
    const fullRightText = rightSnippets.join("");
    if (rightIndex < fullRightText.length) {
      const timeout = setTimeout(() => {
        setRightCode(fullRightText.slice(0, rightIndex + 1));
        setRightIndex(rightIndex + 1);
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      const resetTimeout = setTimeout(() => {
        setRightCode("");
        setRightIndex(0);
      }, 2000);
      return () => clearTimeout(resetTimeout);
    }
  }, [rightIndex]);

  return (
    <div>
        {loading && (
          <div className="loader-overlay">
            <div className="loader-spinner" />
            <div className="loader-text">Loading...</div>
          </div>
        )}
        
        <div ref={cursorRef} className="cursor-dot" />
        <div className="code-sides-wrapper">
          <div className="code-side code-left-side">
            <pre><code>{leftCode}<span className="typing-cursor">|</span></code></pre>
          </div>
          <div className="code-side code-right-side">
            <pre><code>{rightCode}<span className="typing-cursor">|</span></code></pre>
          </div>
        </div>
        {/* Navbar */}
        <nav className="navbar">
          
          <div className="nav-links">
            <a className={`hover-zoom ${activeSection === "about" ? "nav-active" : ""}`} href="#about">About</a>
            <a className={`hover-zoom ${activeSection === "skills" ? "nav-active" : ""}`} href="#skills">Skills</a>
            <a className={`hover-zoom ${activeSection === "projects" ? "nav-active" : ""}`} href="#projects">Projects</a>
            <a className={`hover-zoom ${activeSection === "experience" ? "nav-active" : ""}`} href="#experience">Experience</a>
            <a className={`hover-zoom ${activeSection === "github" ? "nav-active" : ""}`} href="#github">GitHub</a>
            <a className={`hover-zoom ${activeSection === "contact" ? "nav-active" : ""}`} href="#contact">Contact</a>
          </div>
        </nav>

        {/* Hero */}
        <section ref={heroRef} className="hero" style={{ "--hero-offset": `${heroOffset}px` }}>
          <h1 className={`animated-text hover-zoom ${heroInView ? 'fade-in-text' : ''}`}>Hi, I'm Isum</h1>
          
        </section>

        {/* About */}
        <section ref={aboutRef} id="about" className="section1">
          <h1 className={`hover-zoom ${aboutInView ? 'fade-in-text' : ''}`}>About Me</h1>
          <p className={aboutInView ? 'fade-in-text' : ''}>
             I am a Computer Science undergraduate at the University of Colombo who loves
creating things that make life easier for people. I enjoy working with PHP, JavaScript,
React, and Node.js, and I’m always learning something new to improve my skills. I’m
passionate about building real projects, solving problems, and growing into a
well‑rounded developer who can make a positive impact
          </p>
          {/* Animated Statistics */}
          <div className={`stats-container ${aboutInView ? 'fade-in-text' : ''}`}>
            <div className="stat-card">
              <div className="stat-number">{Math.floor(stats.projects)}</div>
              <div className="stat-label">Projects Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.years.toFixed(1)}</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{Math.floor(stats.clients)}</div>
              <div className="stat-label">Happy Clients</div>
            </div>
          </div>        </section>

        {/* Skills */}
        <section ref={skillsRef} id="skills" className="section2">
          <h1 className={skillsInView ? 'fade-in-text' : ''}>Skills</h1>
          <div className="skills-container">
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>PHP</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>JavaScript</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>React</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>Node.js</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>HTML/CSS</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>SQL</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>Tailwind CSS</span>
            <span className={`skill-tag hover-zoom ${skillsInView ? 'fade-in-text' : ''}`}>Cybersecurity</span>
          </div>
        </section>

        {/* Projects */}
        <section ref={projectsRef} id="projects" className="section3">
          <h1 className={`hover-zoom ${projectsInView ? 'fade-in-text' : ''}`}>Projects</h1>
          
          {/* Project Filters */}
          <div className="project-filters">
            {["All", "React", "Node.js", "PHP", "JavaScript", "SQL"].map((tech) => (
              <button
                key={tech}
                className={`filter-btn ${selectedFilter === tech ? "active" : ""}`}
                onClick={() => setSelectedFilter(tech)}
              >
                {tech}
              </button>
            ))}
          </div>

          <div>
            {filteredProjects.map((project, index) => (
              <button
                key={index}
                className={`card card-button ${projectsInView ? 'fade-in-text' : ''}`}
                onClick={() => setSelectedProject(project)}
              >
                <h3 className="hover-zoom">{project.title}</h3>
                <p>{project.description}</p>
                <div className="tech-badges">
                  {project.techs.map((tech) => (
                    <span key={tech} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Project Modal */}
        {selectedProject && (
          <div className="modal-backdrop" onClick={() => setSelectedProject(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setSelectedProject(null)}>×</button>
              <img src={selectedProject.image} alt={selectedProject.title} className="modal-image" />
              <h2>{selectedProject.title}</h2>
              <p>{selectedProject.description}</p>
              <div className="tech-badges" style={{ marginTop: "10px" }}>
                {selectedProject.techs.map((tech) => (
                  <span key={tech} className="tech-badge">{tech}</span>
                ))}
              </div>
              <div className="modal-links">
                <a className="modal-link" href={selectedProject.live} target="_blank" rel="noreferrer">Live Demo</a>
                <a className="modal-link" href={selectedProject.repo} target="_blank" rel="noreferrer">Code</a>
              </div>
            </div>
          </div>
        )}

        {/* Experience */}
        <section ref={experienceRef} id="experience" className="section4">
          <h1 className={`hover-zoom ${experienceInView ? 'fade-in-text' : ''}`}>Experience</h1>
          <table className={`experience-table ${experienceInView ? 'fade-in-text' : ''}`}>
            <thead>
              <tr>
                <th>Role</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Freelance Web Developer</td>
                <td>Full-stack development projects for clients</td>
              </tr>
              <tr>
                <td>Cybersecurity Projects</td>
                <td>Built and tested vulnerable web applications to learn security practices</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* GitHub Stats */}
        <section ref={githubRef} id="github" className="section4">
          <h1 className="hover-zoom">GitHub Stats</h1>
          <div className="github-stats">
            <img
              alt="GitHub Stats"
              className="github-card"
              src="https://github-readme-stats.vercel.app/api?username=iud2003&show_icons=true&theme=graywhite&hide_title=false&hide_border=true&bg_color=00000000&title_color=bfbfbf&text_color=cccccc&icon_color=b1b1b1"
            />
            <img
              alt="Top Languages"
              className="github-card"
              src="https://github-readme-stats.vercel.app/api/top-langs/?username=iud2003&layout=compact&theme=graywhite&hide_border=true&bg_color=00000000&title_color=bfbfbf&text_color=cccccc"
            />
            <img
              alt="GitHub Streak"
              className="github-card"
              src="https://github-readme-streak-stats.herokuapp.com/?user=iud2003&theme=graywhite&hide_border=true&background=00000000&ring=bbbbbb&fire=bbbbbb&currStreakLabel=bfbfbf&sideNums=cccccc&sideLabels=bfbfbf&dates=999999"
            />
          </div>
        </section>

        {/* Contact */}
        <section ref={contactRef} id="contact" className="section5">
          <h1 className={`hover-zoom ${contactInView ? 'fade-in-text' : ''}`}>Contact</h1>
          
          {/* Contact Form */}
          <form className={`contact-form ${contactInView ? 'fade-in-text' : ''}`} onSubmit={handleFormSubmit}>
            <div className="form-group">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className="form-group">
              <textarea
                name="message"
                placeholder="Your Message"
                rows="5"
                value={formData.message}
                onChange={handleFormChange}
                required
              ></textarea>
            </div>
            <button type="submit" className="submit-btn">Send Message</button>
            {formStatus && <p className="form-status">{formStatus}</p>}
          </form>
        </section>

        {/* Back to Top Button */}
        {showBackToTop && (
          <button className="back-to-top" onClick={scrollToTop} title="Back to top">
            ↑
          </button>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="footer-links">
            <a href="mailto:isumuthsara2003@gmail.com" target="_blank" rel="noreferrer">
              <img src="/gmail.png" alt="Email" className="footer-icon" />
            </a>
            <a href="https://github.com/iud2003" target="_blank" rel="noreferrer">
              <img src="/git.png" alt="GitHub" className="footer-icon" />
            </a>
            <a href="https://www.linkedin.com/in/isum-uthsara-dissanayake-a900b5335/" target="_blank" rel="noreferrer">
              <img src="/linked in.png" alt="LinkedIn" className="footer-icon" />
            </a>
            <a href="https://wa.me/94765578700" target="_blank" rel="noreferrer">
              <img src="/whatsapp.png" alt="WhatsApp" className="footer-icon" />
            </a>
          </div>
          <p>© 2025 Isum Uthsara Dissanayake</p>
        </footer>
    </div>
  );
}

export default App;
